import React, { useEffect, useMemo, useRef, useState } from "react";
const inputCls = "w-full px-3 py-2 rounded-md border border-border bg-card focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm";

let catalogsPromise;
function loadCatalogs() {
    if (!catalogsPromise) {
        catalogsPromise = Promise.all([
            fetch("/data/rpf26/municipalities.json").then((r) => {
                if (!r.ok) throw new Error("Catalogo comuni RPF26 non disponibile");
                return r.json();
            }),
            fetch("/data/rpf26/foreign-states.json").then((r) => {
                if (!r.ok) throw new Error("Catalogo Stati esteri RPF26 non disponibile");
                return r.json();
            }),
            fetch("/data/rpf26/ateco-2007.json").then((r) => r.ok ? r.json() : { entries: [] }),
            fetch("/data/rpf26/ateco-2025.json").then((r) => r.ok ? r.json() : { entries: [] }),
        ]).then(([municipalities, foreignStates, ateco2007, ateco2025]) => ({
            municipalities,
            foreignStates,
            ateco2007: ateco2007.entries || [],
            ateco2025: ateco2025.entries || [],
        }));
    }
    return catalogsPromise;
}

const MUNICIPAL_FIELDS = {
    "frontespizio.comune_nascita": { province: "frontespizio.provincia_nascita" },
    "frontespizio.residenza_comune": {
        province: "frontespizio.residenza_provincia", cap: "frontespizio.residenza_cap", code: "frontespizio.residenza_codice_comune",
    },
    "frontespizio.domicilio_fiscale_comune": {
        province: "frontespizio.domicilio_fiscale_provincia", code: "frontespizio.domicilio_fiscale_codice_comune",
    },
    "frontespizio.domicilio_fiscale_2026_comune": {
        province: "frontespizio.domicilio_fiscale_2026_provincia", code: "frontespizio.domicilio_fiscale_2026_codice_comune",
    },
    "frontespizio.rappresentante_comune_residenza": {
        province: "frontespizio.rappresentante_provincia", cap: "frontespizio.rappresentante_cap",
    },
};

const FOREIGN_FIELDS = {
    "frontespizio.estero_stato": "frontespizio.estero_codice_stato",
    "frontespizio.rappresentante_stato_estero": "frontespizio.rappresentante_codice_stato_estero",
};

const FOREIGN_BIRTH_CODES = {
    Z330: ["MAROCCO", "EE"], Z110: ["FRANCIA", "EE"], Z112: ["GERMANIA", "EE"],
    Z129: ["ROMANIA", "EE"], Z133: ["SVIZZERA", "EE"], Z336: ["EGITTO", "EE"],
    Z352: ["TUNISIA", "EE"],
};
const MONTHS = { A: 1, B: 2, C: 3, D: 4, E: 5, H: 6, L: 7, M: 8, P: 9, R: 10, S: 11, T: 12 };
const OMOCODIA = { L: "0", M: "1", N: "2", P: "3", Q: "4", R: "5", S: "6", T: "7", U: "8", V: "9" };

function decodeDigits(value) {
    return value.split("").map((c) => OMOCODIA[c] || c).join("");
}

export function decodeItalianFiscalCode(raw, municipalities) {
    const code = String(raw || "").trim().toUpperCase();
    if (!/^[A-Z0-9]{16}$/.test(code)) return null;
    const yy = Number(decodeDigits(code.slice(6, 8)));
    const month = MONTHS[code[8]];
    let day = Number(decodeDigits(code.slice(9, 11)));
    if (!month || !day || day > 71) return null;
    const sex = day > 40 ? "F" : "M";
    if (sex === "F") day -= 40;
    const currentYY = new Date().getFullYear() % 100;
    const year = (yy <= currentYY ? 2000 : 1900) + yy;
    const placeCode = code[11] + decodeDigits(code.slice(12, 15));
    const municipality = municipalities.find((item) => item.code === placeCode);
    const foreign = FOREIGN_BIRTH_CODES[placeCode];
    return {
        birthDate: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
        sex,
        birthPlace: municipality?.name || foreign?.[0] || "",
        birthProvince: municipality?.province || foreign?.[1] || "",
        placeCode,
    };
}

function labelEntry(item) {
    return `${item.name} (${item.province})${item.caps?.length ? ` · ${item.caps.join("/")}` : ""}`;
}

export function Rpf26SmartInput({ field, path, value, onChange, updField, compact }) {
    const [catalogs, setCatalogs] = useState(null);
    const [query, setQuery] = useState("");
    const lastDecodedFiscalCode = useRef("");
    useEffect(() => { loadCatalogs().then(setCatalogs).catch(() => setCatalogs({ municipalities: [], foreignStates: [], ateco2007: [], ateco2025: [] })); }, []);
    useEffect(() => {
        if (path !== "frontespizio.codice_fiscale" || !catalogs) return;
        const code = String(value || "").trim().toUpperCase();
        if (code.length !== 16 || lastDecodedFiscalCode.current === code) return;
        const decoded = decodeItalianFiscalCode(code, catalogs.municipalities);
        if (!decoded) return;
        lastDecodedFiscalCode.current = code;
        updField("frontespizio.data_nascita", decoded.birthDate);
        updField("frontespizio.sesso", decoded.sex);
        if (decoded.birthPlace) updField("frontespizio.comune_nascita", decoded.birthPlace);
        if (decoded.birthProvince) updField("frontespizio.provincia_nascita", decoded.birthProvince);
    }, [catalogs, path, updField, value]);
    const cls = compact ? inputCls + " !py-1.5 !px-2 text-xs" : inputCls;
    const municipalityConfig = MUNICIPAL_FIELDS[path];
    const foreignCodePath = FOREIGN_FIELDS[path];
    const atecoYear = field.key === "ateco_2025" ? 2025 : field.key === "codice_attivita" ? 2007 : null;
    const matches = useMemo(() => {
        if (!catalogs || query.trim().length < 2) return [];
        const needle = query.trim().toUpperCase().replace(/\./g, "");
        const source = municipalityConfig ? catalogs.municipalities : foreignCodePath ? catalogs.foreignStates : atecoYear === 2025 ? catalogs.ateco2025 : catalogs.ateco2007;
        return source.filter((item) => {
            const code = String(item.code || "").replace(/\./g, "").toUpperCase();
            const name = String(item.name || item.description || "").toUpperCase();
            return code.includes(needle) || name.includes(needle);
        }).slice(0, 80);
    }, [catalogs, query, municipalityConfig, foreignCodePath, atecoYear]);

    if (!municipalityConfig && !foreignCodePath && !atecoYear && path !== "frontespizio.codice_fiscale") return null;
    const listId = `rpf26-${path.replace(/[^a-z0-9]/gi, "-")}`;
    const selectEntry = (raw) => {
        onChange(raw);
        if (!catalogs) return;
        if (municipalityConfig) {
            const selected = catalogs.municipalities.find((item) => item.name.toUpperCase() === raw.toUpperCase() || item.code === raw.toUpperCase());
            if (selected) {
                onChange(selected.name);
                if (municipalityConfig.province) updField(municipalityConfig.province, selected.province || "");
                if (municipalityConfig.cap) updField(municipalityConfig.cap, selected.caps?.length === 1 ? selected.caps[0] : "");
                if (municipalityConfig.code) updField(municipalityConfig.code, selected.code || "");
            }
        } else if (foreignCodePath) {
            const selected = catalogs.foreignStates.find((item) => item.name.toUpperCase() === raw.toUpperCase() || item.code === raw);
            if (selected) { onChange(selected.name); updField(foreignCodePath, selected.code); }
        }
    };
    const handleChange = (raw) => {
        const next = field.upper ? raw.toUpperCase() : raw;
        setQuery(next);
        selectEntry(next);
    };
    return <>
        <input
            data-testid={field.testid}
            className={cls}
            disabled={Boolean(field.readonly)}
            value={value || ""}
            maxLength={field.maxLength}
            placeholder={field.placeholder || (municipalityConfig ? "Digita comune o codice catastale" : foreignCodePath ? "Digita Stato o codice" : atecoYear ? "Digita codice o attività" : undefined)}
            list={listId}
            autoComplete="off"
            onChange={(e) => handleChange(e.target.value)}
        />
        {(municipalityConfig || foreignCodePath || atecoYear) && <datalist id={listId}>
            {matches.map((item) => <option key={`${item.code}-${item.name || item.description}`} value={item.name || item.code}>{municipalityConfig ? labelEntry(item) : `${item.code} · ${item.description || item.name}`}</option>)}
        </datalist>}
        {municipalityConfig && <div className="mt-1 text-[10px] text-muted-foreground">Catalogo PF2026: 7.894 comuni · provincia, CAP e codice catastale automatici.</div>}
        {foreignCodePath && <div className="mt-1 text-[10px] text-muted-foreground">Catalogo PF2026: 260 Stati e territori · codice Paese automatico.</div>}
        {path === "frontespizio.codice_fiscale" && <div className="mt-1 text-[10px] text-muted-foreground">Data, sesso e luogo di nascita vengono ricavati dal codice fiscale quando riconosciuti.</div>}
    </>;
}
